import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { CapitalCallInvestmentSelectCellRenderer } from "features/capital-call/drawdown/CapitalCallDrawdownFields";
import { CreditFacilityEventLayout } from "features/credit-facility/CreditFacilityEventLayout";
import { OutstandingTableChangeStats } from "features/credit-facility/CreditFacilityPaydownPage";
import { CreditFacilitySubpageProps } from "features/credit-facility/CreditFacilitySubpageProps";
import { useGetAllocationsByLookupKey } from "features/drawdown/DrawdownUtils";
import {
  getCreditFacilityDrawdownRetrieveQueryKey,
  getCreditFacilityPrincipalUsesRetrieveQueryKey,
  useCreditFacilityDrawdownCreate,
  useCreditFacilityDrawdownRetrieveSuspense,
  useCreditFacilityRetrieveSuspense,
} from "gen/endpoints/credit-facility/credit-facility";
import { FundFamilyUseAllocationTableColumnEditorSelectDef } from "gen/model/fundFamilyUseAllocationTableColumnEditorSelectDef";
import { FundFamilyUseAllocationTableData } from "gen/model/fundFamilyUseAllocationTableData";
import { FundFamilyUseAllocationTableRow } from "gen/model/fundFamilyUseAllocationTableRow";
import { creditFacilityDrawdownCreateBody } from "gen/schemas/credit-facility/credit-facility";
import { cloneDeep } from "lodash-es";
import React, { useCallback, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router";
import Form from "shared/components/form/Form";
import { FormErrorAlert } from "shared/components/form/FormErrorAlert";
import { FundFamilyUseAllocationTable } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTable";
import { fundFamilyUseAllocationTableResolver } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableFormUtils";
import {
  FundFamilyUseAllocationTableColumnCellRendererProps,
  FundFamilyUseAllocationTableColumnCellRendererSelector,
  FundFamilyUseAllocationTableGetAllocationFn,
} from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableProps";
import {
  PopulateEmptyFundFamilyUseAllocationTableRowFn,
  useFundFamilyUseAllocationTableFormRowController,
} from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableRowController";
import {
  isFundFamilyUseAllocationTableRowEditable,
  useGetFundFamilyUseAllocationTableDataGetErrorForCellFnFromResponse,
} from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableUtils";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { SimpleSelect } from "shared/components/input/select/SimpleSelect";
import {
  PageWizardFormFooter,
  usePageWizard,
} from "shared/components/layout/PageWizard";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { getMaybernTableCellError } from "shared/components/table/maybern/MaybernTableErrors";
import { MaybernTableFocusCellRendererWrapper } from "shared/components/table/maybern/MaybernTableFocusCellRendererWrapper";
import { sumDecimalStrings } from "shared/utils/DecimalUtils";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";
import invariant from "shared/utils/TinyInvariant";
// overwrites the default component to filter out dropdown options based on use_type selected and sets a default code
export const TransactionCodeSelector: React.FC<
  FundFamilyUseAllocationTableColumnCellRendererProps
> = ({ columnDef, ...params }) => {
  const editor =
    columnDef.editor as FundFamilyUseAllocationTableColumnEditorSelectDef;
  const ref = useRef<never>(null);
  invariant(editor.options);
  const data = params.data as FundFamilyUseAllocationTableRow;
  const transactionCodeOptions = editor.options.filter(
    (option) => option.meta?.classification == data.category_id,
  );
  return (
    <MaybernTableFocusCellRendererWrapper params={params} inputRef={ref}>
      <SimpleSelect
        ref={ref}
        title={editor.placeholder || undefined}
        value={params.value}
        onChangeValue={params.setValue}
        variant="simple-table"
        isDisabled={!isFundFamilyUseAllocationTableRowEditable(params.node)}
        error={getMaybernTableCellError(params)}
      >
        {transactionCodeOptions.map(({ label, value }) => (
          <SelectOption value={value} key={value}>
            {label}
          </SelectOption>
        ))}
      </SimpleSelect>
    </MaybernTableFocusCellRendererWrapper>
  );
};

const resolver = fundFamilyUseAllocationTableResolver(
  zodResolver(creditFacilityDrawdownCreateBody),
);

export const CreditFacilityDrawdownPage: React.FC = () => {
  const { t } = useTranslation(["creditFacility"]);
  const { fundFamilyId, creditFacilityId, fundFamilyEvent } =
    useOutletContext<CreditFacilitySubpageProps>();

  const queryClient = useQueryClient();

  const { data: creditFacility } = useCreditFacilityRetrieveSuspense(
    fundFamilyId,
    creditFacilityId,
  );

  const { data: allocationTableData } =
    useCreditFacilityDrawdownRetrieveSuspense(
      fundFamilyId,
      creditFacilityId,
      fundFamilyEvent.id!,
    );

  const {
    mutateAsync: upsertDrawdown,
    error: upsertError,
    variables: upsertVariables,
    reset: upsertReset,
  } = useCreditFacilityDrawdownCreate({
    mutation: {
      onSuccess: async () =>
        await invalidateQueryKeys(
          queryClient,
          getCreditFacilityDrawdownRetrieveQueryKey(
            fundFamilyId,
            creditFacilityId,
            fundFamilyEvent.id!,
          ),
          getCreditFacilityPrincipalUsesRetrieveQueryKey(
            fundFamilyId,
            creditFacilityId,
          ),
        ),
    },
  });

  const populateEmptyRow =
    useCallback<PopulateEmptyFundFamilyUseAllocationTableRowFn>(
      (data) => {
        const newRow: FundFamilyUseAllocationTableRow = cloneDeep(data);
        const transactionCodes = (
          allocationTableData.definition.headers?.find(
            ({ id }) => id == "transaction_code_id",
          )?.editor as FundFamilyUseAllocationTableColumnEditorSelectDef
        ).options;
        // this only works right now as the default is always the first one in the list, we might need to revisit this if that ever changes.
        const defaultTransactionCode = (transactionCodes || []).find(
          ({ meta }) => meta?.classification == data.category_id,
        );
        newRow.column_data = {
          ...newRow.column_data,
          type: "drawdown",
          investment_id: null,
          loan_id: "",
          transaction_code_id: defaultTransactionCode?.value ?? "",
        };
        return newRow;
      },
      [allocationTableData.definition.headers],
    );

  const { navigateNext } = usePageWizard();

  const form = useForm<FundFamilyUseAllocationTableData>({
    defaultValues: { rows: allocationTableData.rows },
    resolver,
  });

  const { rows, onRowChange, onRowRemove, onRowAdd } =
    useFundFamilyUseAllocationTableFormRowController(
      form,
      "rows",
      populateEmptyRow,
    );

  const getErrorForCell =
    useGetFundFamilyUseAllocationTableDataGetErrorForCellFnFromResponse(
      upsertError,
      useCallback(
        (rowId) =>
          upsertVariables?.data.rows.findIndex((it) => it.id === rowId),
        [upsertVariables],
      ),
    );

  const onSubmit = async (data: FundFamilyUseAllocationTableData) =>
    await upsertDrawdown({
      fundFamilyId,
      creditFacilityId,
      fundFamilyEventId: fundFamilyEvent.id!,
      data,
    });

  const allocationsByLookupKey = useGetAllocationsByLookupKey(
    fundFamilyId,
    useMemo(
      () =>
        rows.map((row) => {
          invariant(row.column_data?.type === "drawdown");
          return {
            fund_ids: creditFacility.borrowing_entity_ids,
            transaction_code_id: row.column_data.transaction_code_id || null,
            investment_id: row.column_data.investment_id || null,
          };
        }),
      [rows, creditFacility],
    ),
    fundFamilyEvent.effective_date,
  );

  const getAllocation =
    useCallback<FundFamilyUseAllocationTableGetAllocationFn>(
      (params) => {
        const columnData = params.row.column_data;
        if (columnData?.type === "drawdown") {
          const fundAllocations = allocationsByLookupKey?.({
            transaction_code_id: columnData.transaction_code_id || null,
            investment_id: columnData.investment_id || null,
          });
          const fundInfo = fundAllocations?.find(
            (it) => it.id === params.fundDef.fund_id,
          );
          if (fundInfo) {
            return {
              ownershipPercentage: fundInfo.ownershipPercentage,
              allocationRuleId: fundInfo.allocationRuleId,
              allocationRuleName: fundInfo.allocationRuleName,
            };
          }
        }
        return undefined;
      },
      [allocationsByLookupKey],
    );

  const drawdownAmount = sumDecimalStrings(
    rows.map((row) => row.fund_family["drawdown"].amount),
  ).toString();

  return (
    <CreditFacilityEventLayout canEditEvent={true}>
      <Form
        form={form}
        excludeNonFieldErrors={["rows"]}
        onError={() => true}
        onBeforeSubmit={upsertReset}
        onSubmit={onSubmit}
        onSuccess={navigateNext}
      >
        <FormErrorAlert error={upsertError} mb="16px" />
        <TableBox isFullHeight={true} isFullWidth={true}>
          <TableBoxHeader>
            <TableBoxTitle>{t("creditFacility:drawdown.type")}</TableBoxTitle>
          </TableBoxHeader>
          <OutstandingTableChangeStats
            changeAmount={drawdownAmount}
            eventType={fundFamilyEvent.sub_type!}
          />
          <TableBoxBody>
            <FundFamilyUseAllocationTable
              tableDef={allocationTableData.definition}
              onRowAdd={onRowAdd}
              onRowRemove={onRowRemove}
              onRowChange={onRowChange}
              getAllocation={getAllocation}
              tableData={{ rows }}
              columnCellRendererSelector={columnCellRendererSelector}
              getErrorForCell={getErrorForCell}
            />
          </TableBoxBody>
        </TableBox>
        <PageWizardFormFooter />
      </Form>
    </CreditFacilityEventLayout>
  );
};

const columnCellRendererSelector: FundFamilyUseAllocationTableColumnCellRendererSelector =
  (columnDef, params) => {
    const categoryId = params.data?.category_id;
    if (columnDef.id == "transaction_code_id") {
      return TransactionCodeSelector;
    } else if (
      columnDef.id === "investment_id" &&
      categoryId &&
      categoryId === "investment"
    ) {
      return CapitalCallInvestmentSelectCellRenderer;
    }
    return undefined;
  };
